import React from 'react';
import type { RefObject } from 'react';
import type { CanvasPlugin } from '../../../core/canvas/CanvasPlugin';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { ChangeNodeStyleCommand } from '../../../core/commands/ChangeNodeStyleCommand';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import { Logger } from '../../../utils/logger';
import { CommentOverlays } from '../../comments/CommentOverlays';
import { ContextAwareQuickCreatePanel } from '../../ui/ContextAwareQuickCreatePanel';
import { LinkInputPanel } from '../../ui/LinkInputPanel';
import { Popup } from '../../ui/Popup';
import { ShapeQuickCreatePanel } from '../../ui/ShapeQuickCreatePanel';
import type { Node, Edge } from '../../../core/models/scene';
import { QuickCreateBasicNodeCommand } from '../../../core/commands/QuickCreateBasicNodeCommand';

const canvasLogger = Logger.getLoggerWithPrefix('DiagramCanvasWithTransform');

export type CanvasPopupsProps = {
  stageRef: RefObject<any>;
  positionedComments: any;
  diagramId: any;
  createRootComment: any;
  createReply: any;
  isCreating: any;
  isReplying: any;
  resolveComment: any;
  reopenComment: any;
  isResolving: any;
  isReopening: any;
  updateComment: any;
  isUpdating: any;
  softDeleteComment: any;
  isDeleting: any;
  eventBus: any;
  commandManager: CommandManager;
  currentUserId: any;

  shapePickerState: any;
  handleShapePickerClose: () => void;
  popupStyle: any;
  nodes: Node[];
  edges: Edge[];
  toolRegistry: ToolRegistry;
  connectorTool: any;
  setIsConnecting: (next: boolean) => void;
  setFirstConnectNode: (nodeId: string | null) => void;
  effectiveDiagramType: DiagramType;
  doc: unknown;
  ensureOrgDeps: () => Promise<any>;
  orgReportingTargets: any;
  collapsedNodeIds: string[];
  handleToggleOrgCollapseCommand: (reportingNodeId: string) => void;
  requestConnectorRefresh?: (input: { nodeIds?: string[]; edgeIds?: string[] }) => void;
  canvasPluginsRef: RefObject<CanvasPlugin[]>;

  insertBetweenPicker: any;
  setInsertBetweenPicker: React.Dispatch<React.SetStateAction<any>>;

  linkPrompt: any;
  setLinkPrompt: React.Dispatch<React.SetStateAction<any>>;
};

export const CanvasPopups: React.FC<CanvasPopupsProps> = ({
  stageRef,
  positionedComments,
  diagramId,
  createRootComment,
  createReply,
  isCreating,
  isReplying,
  resolveComment,
  reopenComment,
  isResolving,
  isReopening,
  updateComment,
  isUpdating,
  softDeleteComment,
  isDeleting,
  eventBus,
  commandManager,
  currentUserId,
  shapePickerState,
  handleShapePickerClose,
  popupStyle,
  nodes,
  edges,
  toolRegistry,
  connectorTool,
  setIsConnecting,
  setFirstConnectNode,
  effectiveDiagramType,
  doc,
  ensureOrgDeps,
  orgReportingTargets,
  collapsedNodeIds,
  handleToggleOrgCollapseCommand,
  requestConnectorRefresh,
  canvasPluginsRef,
  insertBetweenPicker,
  setInsertBetweenPicker,
  linkPrompt,
  setLinkPrompt,
}) => {
  return (
    <>
      <CommentOverlays
        stageRef={stageRef}
        comments={positionedComments}
        diagramId={diagramId}
        createRootComment={createRootComment}
        createReply={createReply}
        isCreating={isCreating}
        isReplying={isReplying}
        resolveComment={resolveComment}
        reopenComment={reopenComment}
        isResolving={isResolving}
        isReopening={isReopening}
        updateComment={updateComment}
        isUpdating={isUpdating}
        softDeleteComment={softDeleteComment}
        isDeleting={isDeleting}
        eventBus={eventBus}
        commandManager={commandManager}
        currentUserId={currentUserId}
      />

      {/* Context-aware relationship picker */}
      <Popup
        id="shape-picker"
        visible={shapePickerState.visible}
        onClose={handleShapePickerClose}
        position={shapePickerState.position}
        style={popupStyle}
        closeOnDeselect={true}
        closeOnOutsideClick={true}
      >
        <div
          className="bg-white rounded-lg border border-[#E5E7EB] shadow-lg overflow-hidden"
          data-testid="tid.canvas.quickCreate.panel"
        >
          {shapePickerState.sourceNodeId && shapePickerState.sourceSide && (() => {
            const sourceNode = nodes.find((n) => n.id === shapePickerState.sourceNodeId);
            const nodeType = sourceNode?.type;

            // Handle genogram person shapes
            if (nodeType === 'person') {
              return (
                <ContextAwareQuickCreatePanel
                  diagramType={'genogram' as any}
                  sourceNodeId={shapePickerState.sourceNodeId}
                  direction={shapePickerState.sourceSide as any}
                  canAddParents={(() => {
                    try {
                      const sid = shapePickerState.sourceNodeId!;
                      return !edges.some((e) => e.type === 'child' && e.to === sid);
                    } catch {
                      return false;
                    }
                  })()}
                  onAddParents={async () => {
                    try {
                      await toolRegistry.executeTool('genogram-add-parents');
                    } finally {
                      handleShapePickerClose();
                    }
                  }}
                  onCreateNew={async ({ subtypeId, person }) => {
                    await toolRegistry.executeTool('genogram-quick-relate', {
                      mode: 'new',
                      sourceNodeId: shapePickerState.sourceNodeId,
                      direction: shapePickerState.sourceSide,
                      subtypeId,
                      person,
                    });
                    handleShapePickerClose();
                  }}
                  onLinkExisting={({ subtypeId }) => {
                    handleShapePickerClose();
                    (connectorTool as any).connectionType = subtypeId;
                    setIsConnecting(true);
                    setFirstConnectNode(shapePickerState.sourceNodeId);
                  }}
                />
              );
            }

            if (nodeType) {
              const context = { toolRegistry, diagramType: effectiveDiagramType, commandManager, doc };
              const extra = {
                ensureOrgDeps,
                orgReportingTargets,
                collapsedNodeIds,
                handleToggleOrgCollapseCommand,
                requestConnectorRefresh,
                onClose: handleShapePickerClose,
              };
              for (const plugin of canvasPluginsRef.current ?? []) {
                const panel = plugin.renderQuickCreatePanel?.({
                  nodeType,
                  sourceNode,
                  direction: shapePickerState.sourceSide,
                  context,
                  extra,
                });
                if (panel) return panel;
              }
            }

            // Orgchart/flowchart/BPMN quick-create is delegated to CanvasPlugins.

            // Handle basic shapes and orgchart shapes
            if (nodeType === 'basic-shape' || nodeType === 'org-shape') {
              return (
                <ShapeQuickCreatePanel
                  shapeType={nodeType as 'basic-shape' | 'org-shape'}
                  direction={shapePickerState.sourceSide}
                  onCreateNew={({ variant }) => {
                    if (!sourceNode) return;

                    // Calculate new shape position based on source node and direction
                    const sourceX = sourceNode.x;
                    const sourceY = sourceNode.y;
                    const sourceW = sourceNode.w || (sourceNode as any).width || 120;
                    const sourceH = sourceNode.h || (sourceNode as any).height || 80;
                    // Determine category based on node type
                    let category: 'basic' | 'orgchart' = 'basic';
                    if (nodeType === 'org-shape') category = 'orgchart';

                    const cmd = new QuickCreateBasicNodeCommand({
                      sourceNodeId: shapePickerState.sourceNodeId!,
                      variant,
                      direction: shapePickerState.sourceSide!,
                      sourceX,
                      sourceY,
                      sourceW,
                      sourceH,
                      category,
                    });
                    commandManager.execute(cmd);

                    handleShapePickerClose();
                  }}
                />
              );
            }

            return null;
          })()}
        </div>
      </Popup>

      {/* Insert-between quick create picker (for flowchart connectors) */}
      <Popup
        id="insert-between-picker"
        visible={insertBetweenPicker.visible}
        onClose={() => setInsertBetweenPicker((prev: any) => ({ ...prev, visible: false }))}
        position={insertBetweenPicker.position}
        style={{ transform: 'translate(-50%, 8px)' }}
        closeOnDeselect={true}
        closeOnOutsideClick={true}
      >
        <div className="bg-white rounded-lg border border-[#E5E7EB] shadow-lg overflow-hidden" data-testid="tid.canvas.insertBetween.panel">
          <ShapeQuickCreatePanel
            shapeType={insertBetweenPicker.shapeType ?? 'flowchart-shape'}
            direction="right"
            onCreateNew={({ variant }) => {
              try {
                const edgeId = insertBetweenPicker.edgeId;
                if (!edgeId) return;
                const context = { toolRegistry, diagramType: effectiveDiagramType, commandManager, doc };
                for (const plugin of canvasPluginsRef.current ?? []) {
                  if (!plugin.executeInsertStepBetween) continue;
                  plugin.executeInsertStepBetween({
                    edgeId,
                    variant,
                    midpointX: insertBetweenPicker.midpointX,
                    midpointY: insertBetweenPicker.midpointY,
                    context,
                  });
                  break;
                }
              } finally {
                setInsertBetweenPicker((prev: any) => ({ ...prev, visible: false }));
              }
            }}
          />
        </div>
      </Popup>

      {/* Link input popup */}
      <Popup
        id="link-input"
        visible={linkPrompt.visible}
        onClose={() => setLinkPrompt((prev: any) => ({ ...prev, visible: false }))}
        position={linkPrompt.position}
        style={{ transform: 'translate(-50%, 8px)' }}
        closeOnDeselect={true}
        closeOnOutsideClick={true}
      >
        <LinkInputPanel
          initial={linkPrompt.initial}
          onSubmit={(value) => {
            try {
              if (!linkPrompt.nodeId) return;
              const cmd = new ChangeNodeStyleCommand([linkPrompt.nodeId], 'link', value);
              commandManager.execute(cmd);
            } catch (e) {
              canvasLogger.error('Failed to set link', e);
            }
          }}
          onClose={() => setLinkPrompt((prev: any) => ({ ...prev, visible: false }))}
        />
      </Popup>
    </>
  );
};
